import { ReactNode, useEffect, useState } from "react";
import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import { Timer, CalendarDays, Settings as SettingsIcon } from "lucide-react";
import { useIdentity } from "@/hooks/useIdentity";
import { supabase } from "@/integrations/supabase/client";
import LinkAccountNudge from "@/components/LinkAccountNudge";

const nav = [
  { to: "/", idx: "01", label: "TIMER", icon: Timer },
  { to: "/log", idx: "02", label: "ARCHIVE", icon: CalendarDays },
  { to: "/settings", idx: "03", label: "CONFIG", icon: SettingsIcon },
];

function useClock() {
  const [t, setT] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setT(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return t;
}

const pad = (n: number) => n.toString().padStart(2, "0");

export default function ShellLayout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const t = useClock();
  const id = useIdentity();

  // Lock viewport scrolling only on the Timer route so the instrument
  // panel fits in one screen. Archive & Config keep normal scrolling.
  useEffect(() => {
    const onTimer = pathname === "/";
    document.documentElement.classList.toggle("route-timer", onTimer);
    return () => {
      document.documentElement.classList.remove("route-timer");
    };
  }, [pathname]);

  const signOut = async () => {
    await supabase.auth.signOut();
    // Re-arm a guest identity so the app keeps working post-logout.
    await supabase.auth.signInAnonymously();
    navigate("/auth", { replace: true });
  };


  const sectionLabel =
    pathname === "/" ? "PROTOCOL TIMER"
    : pathname.startsWith("/log") ? "FAST LOG // ARCHIVE"
    : pathname.startsWith("/settings") ? "SYSTEM CONFIGURATION"
    : "MONOLITH FAST";

  const sectionIdx =
    pathname === "/" ? "01"
    : pathname.startsWith("/log") ? "02"
    : pathname.startsWith("/settings") ? "03"
    : "00";

  const stamp = `${t.getFullYear()}.${pad(t.getMonth() + 1)}.${pad(t.getDate())} ${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`;

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Unified top strip: section + identity + clock */}
      <header className="lab-strip flex items-center justify-between gap-2 px-3 py-1.5 text-[10px] tracking-[0.18em]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[hsl(var(--terminal-green))]">●</span>
          <span className="lab-label truncate">
            <span className="text-[hsl(var(--terminal-amber))]">[{sectionIdx}]</span> {sectionLabel}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="hidden sm:inline tabular-nums text-[hsl(var(--terminal-dim))]">{stamp}</span>
          <span className="sm:hidden tabular-nums text-[hsl(var(--terminal-dim))]">{pad(t.getHours())}:{pad(t.getMinutes())}</span>
          {id.isAdmin ? (
            <Link
              to="/admin"
              className="text-[hsl(var(--terminal-amber))] hover:text-[hsl(var(--terminal-red))] transition-colors"
              title="Admin terminal"
            >
              ◆ ADMIN
            </Link>
          ) : id.isAuthenticated ? (
            <button
              onClick={signOut}
              className="text-[hsl(var(--terminal-green))] hover:text-[hsl(var(--terminal-red))] transition-colors"
              title="Sign out"
            >
              ● LOGOUT
            </button>
          ) : (
            <Link
              to="/auth"
              className="text-[hsl(var(--terminal-dim))] hover:text-[hsl(var(--terminal-amber))] transition-colors"
              title="Link operator"
            >
              ○ LINK
            </Link>
          )}
          <span className="blink text-[hsl(var(--terminal-green))]">_</span>
        </div>
      </header>

      <LinkAccountNudge />


      <main className="flex-1 px-3 py-4 pb-24 max-w-screen-sm mx-auto w-full relative">
        {children}
      </main>

      {/* Bottom nav — fixed, mobile-first */}
      <nav
        aria-label="Primary"
        className="fixed bottom-0 inset-x-0 border-t border-[hsl(var(--panel-border))] bg-[hsl(var(--panel))] z-40"
      >
        <ul className="grid grid-cols-3 max-w-screen-sm mx-auto">
          {nav.map(({ to, idx, label, icon: Icon }) => (
            <li key={to} className="border-r border-[hsl(var(--panel-border))] last:border-r-0">
              <NavLink
                to={to}
                end
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 py-3 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                    isActive
                      ? "text-[hsl(var(--terminal-green))] bg-[hsl(var(--panel-inset))] shadow-[inset_0_2px_0_hsl(var(--terminal-green)/0.4)]"
                      : "text-[hsl(var(--terminal-dim))] hover:text-[hsl(var(--foreground))]"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span>
                  <span className="text-[hsl(var(--terminal-amber))]">{idx}</span> {label}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* CRT overlays — controlled by html[data-crt] */}
      <div className="crt-overlay" aria-hidden />
      <div className="crt-vignette" aria-hidden />
    </div>
  );
}
